import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import { ClassificationService } from '../classification/ClassificationService';
import { listedGovernancePolicies } from './PolicyDerivation';
import { loadPolicyView } from './GovernancePolicyData';
import { loadSensitivityAlerts } from './SensitivityAlerts';
import { loadGovernanceIssueFeed } from './GovernanceIssueFeed';
import { GovernanceIssueStore } from './GovernanceIssueStore';
import type { GovernanceIssue } from './PolicyIssues';
import type { GovernancePosture, PolicyView } from './PolicyTypes';

const EMPTY_POSTURE: GovernancePosture = { policyCount: 0, totalTurns: 0, avgConfidence: null, flaggedFindings: 0 };

/**
 * Backs the Governance landing's Policy Register. Policies are live governance-category
 * classifications; their activity (verdict volume, distribution, confidence) and the posture
 * totals come from real interactions-OLAP aggregates. The Issues badge counts the same
 * issue feed the Issues tab lists (with {@link GovernanceIssueStore} statuses applied),
 * so the badge and the tab always agree.
 */
@injectable()
export class GovernanceRegisterViewModel extends BaseViewModel {
    loading = true;
    loaded = false;
    errorMessage: string | null = null;
    policies: PolicyView[] = [];
    posture: GovernancePosture = EMPTY_POSTURE;
    /** The same per-turn issue feed the Issues tab renders — the badge counts from it. */
    private issueFeed: GovernanceIssue[] = [];

    constructor(
        private readonly service: ClassificationService,
        @inject(IAnalyticsGatewayToken) private readonly gateway: IAnalyticsGateway,
        private readonly issueStore: GovernanceIssueStore,
    ) {
        super();
    }

    /** Issues still needing attention (open + investigating) — drives the Issues tab badge. */
    get openIssueCount(): number {
        return this.issueFeed.filter((issue) => this.issueStore.statusOf(issue.key) !== 'resolved').length;
    }

    /**
     * Unresolved live issues assigned to a user — drives the "Assigned to Me" badge.
     * Counted against the feed (not the triage store alone) so assignments orphaned by
     * a deleted policy or an aged-out turn don't linger in the badge.
     */
    assignedCount(assigneeUserId: string): number {
        return this.issueFeed.filter(
            (issue) => this.issueStore.assigneeOf(issue.key) === assigneeUserId && this.issueStore.statusOf(issue.key) !== 'resolved',
        ).length;
    }

    async initialize(): Promise<void> {
        this.loading = true;
        this.errorMessage = null;
        this.notifyChange();
        try {
            await this.issueStore.initialize();
            const all = await this.service.list();
            const policies = listedGovernancePolicies(all);
            const [views, sensitivity, issueFeed] = await Promise.all([
                Promise.all(policies.map((policy) => loadPolicyView(this.gateway, policy))),
                loadSensitivityAlerts(this.gateway),
                loadGovernanceIssueFeed(this.gateway, policies),
            ]);
            this.policies = views;
            this.issueFeed = issueFeed;
            this.posture = this.summarize(views, sensitivity.totalMatched);
        } catch (error) {
            this.errorMessage = error instanceof Error ? error.message : 'Failed to load governance data';
            this.policies = [];
            this.issueFeed = [];
            this.posture = EMPTY_POSTURE;
        } finally {
            this.loading = false;
            this.loaded = true;
            this.notifyChange();
        }
    }

    private summarize(views: PolicyView[], flaggedFindings: number): GovernancePosture {
        const totalTurns = views.reduce((sum, view) => sum + view.turns, 0);
        let confSum = 0;
        let confWeight = 0;
        for (const view of views) {
            if (view.avgConfidence !== null && view.turns > 0) {
                confSum += view.avgConfidence * view.turns;
                confWeight += view.turns;
            }
        }
        return {
            policyCount: views.length,
            totalTurns,
            avgConfidence: confWeight > 0 ? confSum / confWeight : null,
            flaggedFindings,
        };
    }
}
